import { Space } from "antd";
import type { SortOrder } from "antd/es/table/interface";
import { useIntl } from "react-intl";
import PermissionCheck from "~Common/PermissionCheck";
import { CompetitorTypes } from "~enums/competitorTypes";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import DeletePlacingAction from "~pages/FinalStandings/components/DeletePlacingAction";
import EntityColumn from "~pages/FinalStandings/components/EntityColumn";
import NumberEditableColumn from "~pages/FinalStandings/components/NumberEditableColumn";
import PersonColumn from "~pages/FinalStandings/components/PersonColumn";
import { useUpsertPlacing } from "~pages/FinalStandings/hooks/useUpsertPlacing";
import type { PlacingTableRow } from "~pages/FinalStandings/types/placingTableRow";
import type { Entity } from "~types/entity";
import type { Person } from "~types/person";

const sortValues = (a?: number | null, b?: number | null) => {
  const valueA = a ?? Number.POSITIVE_INFINITY;
  const valueB = b ?? Number.POSITIVE_INFINITY;
  return valueA - valueB;
};

export const usePlacingTableColumns = (massEditMode: boolean, deletePlacing: (placingId: string) => void) => {
  const { formatMessage } = useIntl();
  const { competitorType } = useSportConfiguration();

  const { mutateAsync: upsertPlacing } = useUpsertPlacing();

  const handleSave = (row: PlacingTableRow, columnId: string) => {
    return async (value: number | null) => {
      if (row[columnId] === value || (columnId === "resultPlace" && (value ?? null) === null)) return;
      await upsertPlacing({
        placingId: row.placingId,
        upsertData: {
          seasonId: row.seasonId,
          entityId: row.entityId,
          personId: row.personId,
          [columnId]: value,
        },
      });
    };
  };

  return [
    ...(competitorType === CompetitorTypes.Entity
      ? [
          {
            title: formatMessage({ id: "teamname" }),
            dataIndex: "entity",
            render: (entity: Entity) => <EntityColumn entity={entity} />,
          },
        ]
      : []),
    ...(competitorType === CompetitorTypes.Person
      ? [
          {
            title: formatMessage({ id: "person" }),
            dataIndex: "person",
            render: (person: Person) => <PersonColumn person={person} />,
          },
        ]
      : []),
    {
      title: formatMessage({ id: "resultPlace" }),
      dataIndex: "resultPlace",
      render: (value: number, row: PlacingTableRow) => (
        <NumberEditableColumn min={1} defaultValue={value} isEditMode={massEditMode} onSave={handleSave(row, "resultPlace")} />
      ),
      width: 100,
      sorter: (a: PlacingTableRow, b: PlacingTableRow) => sortValues(a.resultPlace, b.resultPlace),
      defaultSortOrder: "ascend" as SortOrder,
      sortDirections: ["ascend", "descend", "ascend"] as SortOrder[],
    },
    {
      title: formatMessage({ id: "points" }),
      dataIndex: "points",
      render: (value: number, row: PlacingTableRow) => (
        <NumberEditableColumn
          min={0}
          defaultValue={value}
          isEditMode={!!row.placingId && massEditMode}
          onSave={handleSave(row, "points")}
        />
      ),
      width: 100,
      sortDirections: ["ascend", "descend", "ascend"] as SortOrder[],
      sorter: (a: PlacingTableRow, b: PlacingTableRow) => sortValues(a.points, b.points),
    },
    {
      title: formatMessage({ id: "prizeMoney" }),
      dataIndex: "prizeMoney",
      sortDirections: ["ascend", "descend", "ascend"] as SortOrder[],
      render: (value: number, row: PlacingTableRow) => (
        <NumberEditableColumn
          min={0}
          defaultValue={value}
          isEditMode={!!row.placingId && massEditMode}
          onSave={handleSave(row, "prizeMoney")}
        />
      ),
      width: 100,
      sorter: (a: PlacingTableRow, b: PlacingTableRow) => sortValues(a.prizeMoney, b.prizeMoney),
    },
    {
      render: (_: any, record: PlacingTableRow) => (
        <Space style={{ columnGap: "5px" }}>
          <PermissionCheck
            permissionKeys={[
              competitorType === CompetitorTypes.Entity ? PermissionNames.SEASON_ENTITY_PLACING : PermissionNames.SEASON_PERSON_PLACING,
            ]}
            permissionOptions={[PermissionOperations.DELETE]}
          >
            <DeletePlacingAction record={record} onDelete={deletePlacing} />
          </PermissionCheck>
        </Space>
      ),
      width: 50,
    },
  ];
};
